import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { mutations, queries, queryKeys } from 'RestAPIs/rapini';

import Button from '@sdb/ui/atoms/Button';
import { DialogActions, TextField } from '@mui/material';

import {
  Dialog,
  showSnackbar,
  VirtualizedAutocomplete
} from 'components/shared';

type Props = {
  title: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  regionalPoolQualificationId: string | undefined;
  setRegionalPoolQualificationId: (
    regionalPoolQualificationId: string | undefined
  ) => void;
  regionalPoolTenantName: string;
  tenantQualificationId: string;
};

export default function MappingQualificationsDialog({
  title,
  open,
  setOpen,
  regionalPoolQualificationId,
  setRegionalPoolQualificationId,
  regionalPoolTenantName,
  tenantQualificationId
}: Props) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { data } = queries.useGetRegionalPoolQualifications(
    regionalPoolTenantName as string,
    {
      enabled: !!regionalPoolTenantName
    }
  );

  const options =
    data
      ?.map((r) => ({
        id: r.id as string,
        name: r.name as string
      }))
      .sort(function (a, b) {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      }) ?? [];

  const { mutate } = mutations.useUpdateRegionalPoolQualificationMapping(
    regionalPoolTenantName as string,
    tenantQualificationId as string
  );

  function onSaveClick() {
    mutate(
      { regionalPoolQualificationId },
      {
        onError: () => {
          showSnackbar({
            severity: 'error',
            message: t('somethingWentWrong')
          });
        },
        onSuccess: () => {
          queryClient.invalidateQueries(
            queryKeys.getRegionalPoolQualificationMappings(
              regionalPoolTenantName
            )
          );
          setOpen(false);
        }
      }
    );
  }
  return (
    <Dialog title={title} open={open} handleClose={() => setOpen(false)}>
      <VirtualizedAutocomplete
        options={options}
        value={
          options?.find((o) => o.id === regionalPoolQualificationId) ?? null
        }
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          return option.name as string;
        }}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        renderInput={(params) => (
          <TextField {...params} label={t('selectQualification')} />
        )}
        autoHighlight
        onChange={(e, value: any) => {
          setRegionalPoolQualificationId(value?.id ?? undefined);
        }}
      />
      <DialogActions className="dialog-actions">
        <Button
          onClick={() => setOpen(false)}
          color="secondary"
          variant="contained"
        >
          {t('cancel')}
        </Button>
        <Button onClick={onSaveClick} color="primary" variant="contained">
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
